import { ReportDataService } from './../report-data.service';
import { Component, OnDestroy, OnInit, Optional } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ReportService, AuthenticationService, ClientService } from '../../_services';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';

import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { ReportType } from '../../_models/reports/report-type.model';
import { ReportCoordinatorFilter } from '../../_models/reports/report-coordinator-filter.model';
import { ReportTutorFilter } from '../../_models/reports/report-tutor-filter.model';
import { ReportStudentFilter } from '../../_models/reports/report-student-filter.model';
import { ReportClient } from '../../_models/reports/report-client.model';
import { takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { buildClientReportFilter, getNativeReportCode } from '../client-report-filter.util';
import { ClientReportService } from '../../_services/client-report.service';
import { BrandInfo } from '../../_models/reports/brand-info.model';
import { MilprReport } from '../../_models/reports/milpr-report.model';
import { ClientReportFilter } from '../../_models/reports/client-report-filter.model';
import { ALL_SESSION_REPORT_STATUS_IDS, SESSION_REPORT_STATUSES } from '../../_constants/session-report-statuses';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { ReportFilterLookupsService } from '../report-filter-lookups.service';
import { ReportFileFormat } from '../report-format-picker/report-format-picker.component';
import { ClientReportDownload } from '../client-report-download';
import { FilterSpec, FilterState } from '../../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../../shared/filter-ux/screen-filter-ux';
import { DatePreset, isDatePreset, parseIsoDate, resolveDatePreset, toIsoDate } from '../../shared/filter-ux/date-presets';
import { DatePresetSelection } from '../../shared/filter-ux/date-presets.component';


@Component({
    selector: 'reports-view',
    templateUrl: 'reports-view.component.html',
    styleUrls: ['reports-view.component.css'],
    standalone: false
})
export class ReportsViewComponent implements OnInit, OnDestroy {

  public frmReportSearch: UntypedFormGroup;

  public errorMsg: string;
  public displayedColumns: string[] = ['name', 'generate'];
  public generated: boolean = false;

  public studentTooltip: string

  public filteredReports: Array<ReportType> = new Array<ReportType>();
  public filteredCoordinatorList: Array<ReportCoordinatorFilter> = new Array<ReportCoordinatorFilter>();
  public filteredTutorList: Array<ReportTutorFilter> = new Array<ReportTutorFilter>();
  public filteredStudentList: Array<ReportStudentFilter> = new Array<ReportStudentFilter>();
  public filteredClientList: Array<ReportClient> = new Array<ReportClient>();

  public brandList: BrandInfo[] = [];
  // Session report-status filter (Attendance / MILPR). Defaults to every status so an untouched form
  // returns exactly what it did before the filter existed.
  public readonly sessionStatusList = SESSION_REPORT_STATUSES;
  public nativeReportCode: string = null;
  public nativeRows: any[] = null;
  public nativeMilpr: MilprReport = null;
  /** The Format choice (reports.client-pdf, go-live snag 49). Off until the switch says on; then Excel unless PDF is picked. */
  public pdfEnabled = false;
  public downloadFormat: ReportFileFormat = 'xlsx';
  /** A PDF is being prepared in the background (reports.pdf-background); the screen says so until it downloads. */
  public get preparingPdf(): boolean { return this.downloads.preparing; }
  /** How often the background PDF is checked; a spec shortens it. */
  public get pdfPollMs(): number { return this.downloads.pollMs; }
  public set pdfPollMs(ms: number) { this.downloads.pollMs = ms; }

  private unsubscribe: Subject<void> = new Subject<void>();
  private lastNativeFilter: ClientReportFilter = null;

  /** Loads the filter lists for the chosen client; ReportLookupNoteComponent shows a failed one. */
  private filterLookups: ReportFilterLookupsService;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private reportService: ReportService,
    private authService: AuthenticationService,
    public reportDataService: ReportDataService,
    public themeService: ThemeService,
    private successModalHelper: SuccessModalHelperService,
    private errorModalHelper: ErrorModalHelperService,
    private clientReportService: ClientReportService,
    @Optional() private featureSwitches?: FeatureSwitchService,
    @Optional() filterLookups?: ReportFilterLookupsService,
    @Optional() private router: Router = null,
    @Optional() private route: ActivatedRoute = null
  ) {
    this.filterLookups = filterLookups ?? new ReportFilterLookupsService(reportService, reportDataService);
    this.downloads = new ClientReportDownload(clientReportService, errorModalHelper, this.unsubscribe);
  }

  /** Excel and PDF downloads of the viewed report, shared with the combined Reports page. */
  private readonly downloads: ClientReportDownload;

  /** True when the selected report runs natively (only those have a PDF version). */
  public get isNativeReportSelected(): boolean {
    return getNativeReportCode(this.frmReportSearch?.value?.reports) != null;
  }

  /** The Download button names the format it will produce. */
  public get downloadLabel(): string {
    return this.pdfEnabled && this.downloadFormat === 'pdf' ? 'Download (PDF)' : 'Download (Excel)';
  }

  /**
   * The filters.reports-view switch: date presets, remember-my-filters, Clear all, filter state in the URL and the
   * filter-bar layout, as on Reports Sent. Off, the screen is exactly as it was.
   */
  public readonly filtersKey = FeatureSwitchKeys.filtersReportsView;
  public filtersEnabled: boolean = false;
  /** The date preset behind the start and end dates, or null when they were set by hand. */
  public datePreset: DatePreset | null = null;

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates only:
   * names stay out of the URL and browser storage.
   */
  private static readonly filterSpec: FilterSpec = {
    from: 'string', to: 'string', preset: 'string', report: 'string', client: 'number', coordinator: 'number',
    tutor: 'number', student: 'number[]', brand: 'number[]'
  };
  /** Remembered filters and filter state in the URL; used only while filters.reports-view is on. */
  private filterUx: ScreenFilterUx;
  /** Filters to restore once both the switch and the report types have arrived. */
  private pendingFilterState: FilterState = null;

  ngOnInit() {
    this.filterUx = new ScreenFilterUx(this.filtersKey, ReportsViewComponent.filterSpec, this.router, this.route);

    let defaultEndDate = new Date();
    let defaultStartDate = new Date(defaultEndDate.getTime() - (30*24*60*60*1000));

    this.frmReportSearch = this.formBuilder.group({
      startDate: [defaultStartDate],
      endDate: [defaultEndDate],
      coordinators: [null],
      clients: [null, [Validators.required]],
      tutors: [null],
      students: [null],
      reports: [null, [Validators.required]],
      brands: [null],
      statuses: [[...ALL_SESSION_REPORT_STATUS_IDS]],
   //   referrer : [null]
    });

    this.errorMsg = '';

    this.reportDataService.reportListUpdated().pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.filteredReports = this.reportDataService.reports;
      this.applyPendingFilterState();
    });
    this.reportDataService.clientListUpdated().pipe(takeUntil(this.unsubscribe)).subscribe(() => this.filteredClientList = this.reportDataService.clientList);

    setTimeout(() => {
      this.studentTooltip = "Please select a learner";
    }, 100);

    this.clientReportService.getBrands().subscribe((data: BrandInfo[]) => {
      this.brandList = data != null ? data : [];
    });
    this.featureSwitches?.isEnabled(FeatureSwitchKeys.clientReportsPdf).pipe(takeUntil(this.unsubscribe))
      .subscribe(enabled => this.pdfEnabled = enabled === true);
    // Only native reports have a PDF version, so a switch to any other report falls back to Excel.
    this.frmReportSearch.controls['reports'].valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe(report => {
        if (getNativeReportCode(report) == null) {
          this.downloadFormat = 'xlsx';
        }
      });

    // After the form exists: a restored filter may run the report straight away.
    this.featureSwitches?.isEnabled(this.filtersKey).pipe(takeUntil(this.unsubscribe)).subscribe(enabled => {
      this.filtersEnabled = enabled === true;
      // A shared link's filters, else this user's last ones. Read only once the switch is known to be on.
      this.pendingFilterState = this.filtersEnabled ? this.filterUx.initialState() : null;
      this.applyPendingFilterState();
    });
  }

  /** Clear all: back to the screen's defaults, forgotten, and out of the URL. The report shown is left as it is. */
  public clearAll(): void {
    const defaultEndDate = new Date();
    this.frmReportSearch.reset({
      startDate: new Date(defaultEndDate.getTime() - (30*24*60*60*1000)),
      endDate: defaultEndDate,
      statuses: [...ALL_SESSION_REPORT_STATUS_IDS]
    });
    this.datePreset = null;
    this.filterUx.clear();
  }

  public onDatePreset(selection: DatePresetSelection): void {
    this.datePreset = selection.preset;
    this.frmReportSearch.patchValue({ startDate: parseIsoDate(selection.from), endDate: parseIsoDate(selection.to) });
  }

  /** A preset is remembered as the preset, so it resolves afresh on the next visit. */
  private filterState(): FilterState {
    const value = this.frmReportSearch.value;
    const students = value.students == null ? [] : (Array.isArray(value.students) ? value.students : [value.students]);
    return {
      preset: this.datePreset,
      from: this.datePreset || !value.startDate ? null : toIsoDate(new Date(value.startDate)),
      to: this.datePreset || !value.endDate ? null : toIsoDate(new Date(value.endDate)),
      report: value.reports?.name ?? null,
      client: value.clients,
      coordinator: value.coordinators,
      tutor: value.tutors,
      // Learner ids only: by name, the legacy learner filter would put a name in the URL.
      student: students.filter((id: any) => typeof id === 'number'),
      brand: value.brands ?? []
    };
  }

  /**
   * Restores remembered or linked filters. The report type is an object from the report list, so this waits for
   * the list. With a report type and a client restored the report runs, as it would after View.
   */
  private applyPendingFilterState(): void {
    const state = this.pendingFilterState;
    if (!state || !this.frmReportSearch || (this.reportDataService.reports ?? []).length === 0) {
      return;
    }
    this.pendingFilterState = null;

    this.datePreset = isDatePreset(state.preset) ? state.preset : null;
    const range = this.datePreset ? resolveDatePreset(this.datePreset) : null;
    const from = parseIsoDate(range ? range.from : state.from as string);
    const to = parseIsoDate(range ? range.to : state.to as string);
    const report = this.reportDataService.reports.find(r => r.name === state.report) ?? null;
    const students = (state.student as number[]) ?? [];
    this.frmReportSearch.patchValue({
      startDate: from ?? this.frmReportSearch.value.startDate,
      endDate: to ?? this.frmReportSearch.value.endDate,
      reports: report,
      clients: (state.client as number) ?? null,
      coordinators: (state.coordinator as number) ?? null,
      tutors: (state.tutor as number) ?? null,
      students: students.length > 0 ? students : null,
      brands: ((state.brand as number[]) ?? []).length > 0 ? state.brand : null
    });
    if (state.client != null) {
      this.onClientSelected(state.client);
    }

    if (report && this.frmReportSearch.valid) {
      this.viewReports();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public viewReports() {
    if (this.frmReportSearch.invalid) {
      this.frmReportSearch.markAllAsTouched();
      return;
    }
    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }
    const nativeCode = getNativeReportCode(this.frmReportSearch.value.reports);
    if (nativeCode != null) {
      this.runNativeReport(nativeCode);
    }
  }

  public clearMoreOptions(): void {
    this.frmReportSearch.controls['coordinators'].reset();
    this.frmReportSearch.controls['tutors'].reset();
    this.frmReportSearch.controls['students'].reset();
    if (this.frmReportSearch.controls['brands'] != null) {
      this.frmReportSearch.controls['brands'].reset();
    }
    this.frmReportSearch.controls['statuses'].setValue([...ALL_SESSION_REPORT_STATUS_IDS]);
  }

  /** The status filter only means anything on the session-grain reports. */
  get showStatusFilter(): boolean {
    const code = getNativeReportCode(this.frmReportSearch.value.reports);
    return code === 'attendance' || code === 'milpr';
  }

  public onClientSelected(value: any): void {
    // A lookup that fails leaves its list empty with an inline Retry note, never an error popup (40760).
    this.filterLookups.load(value);
  }

  private runNativeReport(code: string): void {
    this.generated = false;
    this.nativeRows = null;
    this.nativeMilpr = null;
    const filter = buildClientReportFilter(this.frmReportSearch.value, this.frmReportSearch.value.students);
    this.lastNativeFilter = filter;
    const onError = (error: any) => {
      this.errorModalHelper.show("An error has occurred, please try again", error);
      console.error(error);
    };
    if (code === 'milpr') {
      this.clientReportService.runMilpr(filter).subscribe(data => {
        this.nativeMilpr = data;
        this.nativeReportCode = code;
        this.generated = true;
      }, onError);
      return;
    }
    const run: Observable<any[]> = code === 'attendance' ? this.clientReportService.runAttendance(filter)
      : code === 'cohortprogress' ? this.clientReportService.runCohortProgress(filter)
      : this.clientReportService.runProgressOverview(filter);
    run.subscribe((data: any[]) => {
      this.nativeRows = data;
      this.nativeReportCode = code;
      this.generated = true;
    }, onError);
  }

  /** Downloads the viewed report in the chosen format. */
  public downloadReport(): void {
    if (this.pdfEnabled && this.downloadFormat === 'pdf') {
      this.downloadNativePdf();
    } else {
      this.exportNativeReport();
    }
  }

  public exportNativeReport(): void {
    if (this.nativeReportCode == null || this.lastNativeFilter == null) { return; }
    this.downloads.excel(this.nativeReportCode, this.lastNativeFilter);
  }

  /**
   * The same report and filter as the Excel download, as a PDF. Only while reports.client-pdf is on. A report large
   * enough to be prepared in the background (reports.pdf-background) comes back as a job: the screen says so, polls
   * it, and downloads the file when it is ready (ClientReportDownload). A refusal or failure shows the API's message.
   */
  public downloadNativePdf(): void {
    if (!this.pdfEnabled || this.preparingPdf || this.nativeReportCode == null || this.lastNativeFilter == null) { return; }
    this.downloads.pdf(this.nativeReportCode, this.lastNativeFilter);
  }
}
